import CloseIcon from '@material-ui/icons/ClearOutlined' // We dont want to use MUI but the ps-ui one currently doesnt fit the design
import { ButtonAction, chatDrawerActions, EXECUTE_ACTION } from 'ducks/chatDrawer'
import { Box, CheckContainedIcon, IconButton, keyframes, styled, Tooltip, Typography } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useChatScroll } from './ChatScrollContext'
import {
  hideActionAccepted,
  requiresDesignTab,
  requiresSelectedSystem,
  requiresStudioLoaded,
} from './actionPermissions'
import { useActionRequirements } from './hooks/useActionRequirements'
import {
  buildActionDescription,
  buildTooltipMessage,
  getActionIcon,
  getActionTitleFromAction,
} from './utils/actionHelpers'
import { getSelectedSystem, validateActionRequirementsOrThrow } from './utils/validateActionRequirements'
interface ActionConfirmationProps {
  buttonActions: ButtonAction[]
  messageIndex: number
}

const shimmer = keyframes`
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
`

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-4px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

const Container = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '10px 12px',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  borderRadius: '10px',
  marginTop: '12px',
  border: '1px solid rgba(23, 81, 208, 0.12)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
  position: 'relative',
  overflow: 'hidden',
  animation: `${fadeIn} 0.3s ease-out`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent 0%, rgba(23, 81, 208, 0.08) 50%, transparent 100%)',
    animation: `${shimmer} 3s linear infinite`,
    pointerEvents: 'none',
  },
})

const IconWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  borderRadius: '6px',
  background: 'linear-gradient(135deg, rgba(23, 81, 208, 0.1) 0%, rgba(60, 145, 230, 0.1) 100%)',
  flexShrink: 0,
})

const ButtonGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  flexShrink: 0,
  position: 'relative',
  zIndex: 1,
})

const ActionTitle = styled(Typography)({
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  background: 'linear-gradient(90deg, #1751D0 0.01%, #3C91E6 98.75%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  fontSize: '13px',
  lineHeight: '18px',
})

const StyledIconButton = styled(IconButton)({
  width: '32px',
  height: '32px',
  padding: '6px',
})

const ConfirmIconButton = styled(StyledIconButton)({
  background: 'linear-gradient(90deg, #1751D0 0.01%, #3C91E6 98.75%)',
  color: 'white',
  boxShadow: '0 2px 6px rgba(23, 81, 208, 0.25)',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'linear-gradient(90deg, #1a5ae0 0.01%, #4a9ef0 98.75%)',
    boxShadow: '0 3px 10px rgba(23, 81, 208, 0.35)',
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&:disabled': {
    background: '#e0e0e0',
    color: '#9e9e9e',
    boxShadow: 'none',
    cursor: 'not-allowed',
    '&:hover': {
      background: '#e0e0e0',
      boxShadow: 'none',
      transform: 'none',
    },
  },
})

const CancelIconButton = styled(IconButton)({
  width: '32px',
  height: '32px',
  padding: '6px',
  color: '#9CA3AF',
  transition: 'color 200ms ease, background-color 200ms ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    color: '#6B7280',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  '& svg': {
    width: '16px',
    height: '16px',
  },
})

const CollapsedText = styled(Typography)({
  fontSize: '12px',
  color: '#666',
  marginTop: '8px',
  fontStyle: 'italic',
})

export const ActionConfirmation: React.FC<ActionConfirmationProps> = ({ buttonActions, messageIndex }) => {
  const dispatch = useDispatch()
  const translate = useTranslate()
  const { scrollToBottom } = useChatScroll()

  // For now, handle the first action. In the future, we could support multiple actions
  const buttonAction = buttonActions?.[0]
  const action = buttonAction?.action
  const actionType = action?.action || ''

  // Call hook before any early returns to satisfy Rules of Hooks
  const validationState = useActionRequirements(action)

  // Scroll to bottom when action confirmation is shown
  useEffect(() => {
    // Small delay to ensure the component is rendered before scrolling
    const timeoutId = setTimeout(() => {
      scrollToBottom()
    }, 100)
    return () => clearTimeout(timeoutId)
  }, [scrollToBottom])

  if (!buttonActions || buttonActions.length === 0) {
    return null
  }

  // Get collapsed state from Redux (persists across re-renders)
  const isCollapsed = buttonAction.isCollapsed || false
  const confirmedActionLabel = buttonAction.confirmedActionLabel || null

  // Get description and title using helper functions
  const description = buildActionDescription(action, translate)
  const title = getActionTitleFromAction(action, buttonAction.label, translate)

  const handleConfirm = () => {
    // Re-validate before executing (hook will handle updates, but we check current state)
    if (!validationState.isValid) {
      return
    }

    try {
      const pathname = window.location.pathname
      const selectedSystem = requiresSelectedSystem(actionType) ? getSelectedSystem() : undefined
      validateActionRequirementsOrThrow(actionType, pathname, selectedSystem)
    } catch (error: any) {
      console.error('Action validation failed on confirm:', error.message)
      return
    }

    dispatch({ type: EXECUTE_ACTION, payload: action })
    dispatch(chatDrawerActions.updateButtonActionState(messageIndex, 0, true, buttonAction.label))
  }

  const handleCancel = () => {
    dispatch(chatDrawerActions.updateButtonActionState(messageIndex, 0, true, null))
  }

  // Show collapsed state as simple text (only if action was confirmed)
  if (isCollapsed) {
    if (confirmedActionLabel) {
      // Don't show "Action Accepted" for actions that immediately show a progress indicator
      // (like auto-design which shows AutoDesignProgressItem) to avoid flashing
      if (hideActionAccepted(actionType)) {
        return null
      }

      const isAutoAccepted = buttonAction.isAutoAccepted || false
      const translatedLabel = translate(confirmedActionLabel, { _: confirmedActionLabel })
      const acceptedText = isAutoAccepted
        ? translate('Action Auto Accepted: %{label}', { label: translatedLabel })
        : translate('Action Accepted: %{label}', { label: translatedLabel })

      return <CollapsedText textVariant="body2">{acceptedText}</CollapsedText>
    }
    // If cancelled, don't show anything
    return null
  }

  const tooltipMessage = buildTooltipMessage(title, description, action, translate)
  const ActionIcon = getActionIcon(action || actionType)

  // Only show tooltip if title is not the default fallback
  const hasCustomTitle = title !== translate('Confirm Action')
  const titleContent = (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flex: 1,
        minWidth: 0,
        position: 'relative',
        zIndex: 1,
        width: '100%',
      }}
    >
      <IconWrapper>{ActionIcon}</IconWrapper>
      <ActionTitle textVariant="body2">{title}</ActionTitle>
    </span>
  )

  const hasRequirements =
    requiresDesignTab(actionType) || requiresStudioLoaded(actionType) || requiresSelectedSystem(actionType)
  const isDisabled = hasRequirements && !validationState.isValid

  return (
    <Container>
      {hasCustomTitle ? (
        <Tooltip title={title} message={tooltipMessage} placement="top">
          {titleContent}
        </Tooltip>
      ) : (
        titleContent
      )}
      <ButtonGroup>
        <Tooltip title={translate('Cancel')} message={translate('Cancel this action')} placement="top">
          <CancelIconButton onClick={handleCancel} size="small" aria-label={translate('Cancel')}>
            <CloseIcon />
          </CancelIconButton>
        </Tooltip>
        <Tooltip
          title={isDisabled ? validationState.message : translate(buttonAction.label, { _: buttonAction.label })}
          message={isDisabled ? undefined : tooltipMessage}
          placement="top"
          PopperProps={{ style: { zIndex: 2001 } }}
        >
          <span>
            <ConfirmIconButton
              onClick={handleConfirm}
              size="small"
              aria-label={translate(buttonAction.label, { _: buttonAction.label })}
              disabled={isDisabled}
            >
              <CheckContainedIcon width="20px" height="20px" fill="white" />
            </ConfirmIconButton>
          </span>
        </Tooltip>
      </ButtonGroup>
    </Container>
  )
}
